import { AloDbBase, IAloDbBaseConstParam } from '@/models/alodb/AloDbBase'
import { AloBaseArray } from '@/models/alo/AloBaseArray'
import { AloLogbookItem } from '@/models/alo/AloLogbookItem'
import { calculSecurityRights } from '@/enum/SecurityItems'
import { AloSecurityItem } from '@/models/alo/AloSecurityItem'
import { FleetType } from '@/enum/FleetType'
import { getDefaultFleetParamBase, IFleetParamBase } from '../iris/fleet/IFleetParamBase'
import { getDefaultFleetParamSimulation, IFleetParamFormation } from '../iris/fleet/IFleetParamFormation'
import { getDefaultFleetParamCoverage, IFleetParamCoverage } from '../iris/fleet/IFleetParamCoverage'
import { getDefaultFleetIrisConfigCoverage, IFleetIrisConfigCoverage } from '../iris/fleet/IFleetIrisConfigCoverage'
import { getDefaultFleetIrisConfigFormation, IFleetIrisConfigFormation } from '../iris/fleet/IFleetIrisConfigFormation'
import { FleetMode } from '@/enum/FleetMode'
import { ISmartResultGridCoverage } from '../iris/smartMission/ISmartResultGridCoverage'
import { AloDbMissionGlobal } from './AloDbMissionGlobal'
import Vue from 'vue'

export type AloDbFleetCoverage = AloDbFleet & { param: IFleetParamCoverage } & { config: IFleetIrisConfigCoverage }
export type AloDbFleetFormation = AloDbFleet & { param: IFleetParamFormation } & { config: IFleetIrisConfigFormation }
export type AloDbFleetFree = AloDbFleet & { param: IFleetParamBase } & { config: null }

export function isAloDbFleetCoverage(toBeDetermined: any): toBeDetermined is AloDbFleetCoverage {
  if (
    (toBeDetermined as AloDbFleetCoverage).FLE_TYP == FleetType.COVERAGE ||
    (toBeDetermined as AloDbFleetCoverage)?.config?.fleet_type == FleetMode.PATHCOVERAGE
  ) {
    return true
  }
  return false
}

export function isAloDbFleetFormation(toBeDetermined: any): toBeDetermined is AloDbFleetFormation {
  if (
    (toBeDetermined as AloDbFleetFormation).FLE_TYP == FleetType.FORMATION ||
    (toBeDetermined as AloDbFleetFormation)?.config?.fleet_type == FleetMode.KEEPFORMATION
  ) {
    return true
  }
  return false
}

export function isAloDbFleetFree(toBeDetermined: any): toBeDetermined is AloDbFleetFree {
  if ((toBeDetermined as AloDbFleetFree).FLE_TYP == FleetType.FREE) {
    return true
  }
  return false
}

export class AloDbFleet extends AloDbBase {
  public FLE_ID: number | null
  public FLE_LIBELLE: string | null
  public FLE_NAME: string | null
  public FLE_COMPANY: string | null
  public FLE_TYP: FleetType
  public ENS_ID: number | null
  public FLE_PARAM_JSON: string | null
  public FLE_IRIS_CONFIG_JSON: string | null
  public FLE_IRIS_RESULT_GRID_JSON: string | null
  public MISSION_IDS: Array<number>
  public roles: Array<string> //role de l'utilisateur identifié pour la flotte
  public security: { [index: string]: AloSecurityItem }
  public isTestFleet: boolean

  public param: IFleetParamCoverage | IFleetParamFormation | IFleetParamBase
  public config: IFleetIrisConfigCoverage | IFleetIrisConfigFormation | null
  public resultGridJson: ISmartResultGridCoverage | null

  public activeMissionIds: Array<number>

  constructor(
    {
      FLE_ID,
      FLE_NAME,
      FLE_COMPANY,
      FLE_TYP = FleetType.FREE,
      FLE_PARAM_JSON,
      FLE_IRIS_RESULT_GRID_JSON,
      FLE_IRIS_CONFIG_JSON,
      FLE_LIBELLE,
      ENS_ID,
      MISSION_IDS = [],
      roles = ['pilot'],
      isTestFleet = false,
      ...args
    } = {} as any,
  ) {
    super(args)
    this.FLE_ID = FLE_ID
    this.FLE_LIBELLE = FLE_LIBELLE
    this.FLE_NAME = FLE_NAME
    this.FLE_COMPANY = FLE_COMPANY
    this.FLE_TYP = FLE_TYP
    this.FLE_PARAM_JSON = FLE_PARAM_JSON
    this.FLE_IRIS_CONFIG_JSON = FLE_IRIS_CONFIG_JSON
    this.FLE_IRIS_RESULT_GRID_JSON = FLE_IRIS_RESULT_GRID_JSON
    this.ENS_ID = ENS_ID
    this.MISSION_IDS = MISSION_IDS
    this.roles = roles
    this.security = calculSecurityRights(roles)
    this.isTestFleet = isTestFleet

    this.param = AloDbFleet.parseFleetParam(FLE_PARAM_JSON, FLE_TYP)
    this.config = AloDbFleet.parseFleetIrisConfig(FLE_IRIS_CONFIG_JSON, FLE_TYP)

    AloDbFleet.correctGliderAssociation(this)
    if (isAloDbFleetFree(this)) {
      this.activeMissionIds = [...new Set([...(this.param?.freeMissionIds ?? []), ...this.MISSION_IDS])]
      this.param.associationIds = []
    } else {
      this.activeMissionIds = [
        ...new Set([...(this.param?.associationIds ?? []), ...(this.param?.freeMissionIds ?? []), ...this.MISSION_IDS]),
      ]
    }
    AloDbFleet.correctMissionIds(this)

    if (isAloDbFleetCoverage(this))
      this.resultGridJson = AloDbMissionGlobal.parseIrisResultGridCoverage(FLE_IRIS_RESULT_GRID_JSON)
    else this.resultGridJson = null
  }

  static correctMissionIds(fleet: AloDbFleet) {
    //on ajoute les id de missions de MISSION_IDS à param.missionIds si il n'existe pas déjà
    if (fleet.param && fleet.param.missionIds) {
      if (isAloDbFleetFree(fleet)) {
        fleet.param.missionIds = [...new Set([...fleet.param.freeMissionIds, ...fleet.MISSION_IDS])]
      } else {
        fleet.param.missionIds = [
          ...new Set([
            ...fleet.param.missionIds,
            ...fleet.param.freeMissionIds,
            ...fleet.param.associationIds,
            ...fleet.MISSION_IDS,
          ]),
        ]
      }
    }

    if (fleet.param && fleet.param.missionHisto) {
      fleet.param.missionHisto = fleet.param.missionHisto.map(mh => {
        if (mh.endDate == null && !fleet.activeMissionIds.includes(mh.misId)) {
          return { ...mh, endDate: new Date().toISOString() }
        }
        return mh
      })
      //on ajoute les id de missions de activeMissionIds à param.missionHisto si il n'existe pas avec une date endDate à null
      fleet.activeMissionIds.forEach(misId => {
        if (!fleet.param.missionHisto.some(mh => mh.misId == misId && mh.endDate == null)) {
          fleet.param.missionHisto.push({ misId, startDate: new Date().toISOString(), endDate: null })
        }
      })
    }

    //met à jour les argos
    fleet.param.argos = fleet.param.missionIds.reduce((argos, misId) => {
      argos[misId] = fleet.param.argos?.[misId] || ''
      return argos
    }, {} as Record<number, string>)
  }

  static correctGliderAssociation(fleet: AloDbFleet) {
    if (isAloDbFleetFormation(fleet) || isAloDbFleetCoverage(fleet)) {
      //on ajoute ou retire à config.glider_association les cle GLIDER[num] manquante en fonction du nombre de gliders config.glider_number
      const dicAssociation = Object.keys(fleet.config.glider_association).reduce((dic, key) => {
        dic[fleet.config.glider_association[key]?.at(0)?.toString() ?? key] = fleet.config.glider_association[key]
        return dic
      }, {} as Record<string, any>)

      const dicPosition = Object.keys(fleet.config.glider_association).reduce((dic, key) => {
        dic[fleet.config.glider_association[key]?.at(0)?.toString() ?? key] = parseInt(key.replace('GLIDER', ''))
        return dic
      }, {} as Record<string, number>)

      const gliderAssociation = fleet.config.glider_association
      const nbGlider = isAloDbFleetFormation(fleet)
        ? fleet.config.glider_number
        : Object.keys(fleet.config.gliders_config).length
      const gliderKeys = Object.keys(gliderAssociation)

      const gliderConfigKeys = (isAloDbFleetCoverage(fleet) ? Object.keys(fleet.config.gliders_config) : []).sort(
        (a, b) => (dicPosition[a] ?? Number.MAX_SAFE_INTEGER) - (dicPosition[b] ?? Number.MAX_SAFE_INTEGER),
      )

      //on retire les gliders qui ne sont pas dans le nombre de gliders
      for (let i = 0; i < gliderKeys.length; i++) {
        const gliderKey = gliderKeys[i]
        const gliderNumber = parseInt(gliderKey.replace('GLIDER', ''))
        if (gliderNumber > nbGlider) {
          delete gliderAssociation[gliderKey]
        }
      }
      //on ajoute les gliders manquants
      for (let i = 0; i < nbGlider; i++) {
        const gliderKey = `GLIDER${i + 1}`
        if (!gliderAssociation[gliderKey]) {
          Vue.set(gliderAssociation, gliderKey, isAloDbFleetFormation(fleet) ? [null, null, [0, 0]] : [null, null])
        }
        // et reaffecte les config si match
        if (isAloDbFleetCoverage(fleet) && gliderConfigKeys.length > i) {
          const confKey = gliderConfigKeys[i]
          if (dicAssociation[confKey]) gliderAssociation[gliderKey] = dicAssociation[confKey]
        }
      }
    }
  }

  static updateGliderAssociationOffset(fleet: AloDbFleetFormation, offset: Array<[number, number]>) {
    if (isAloDbFleetFormation(fleet)) {
      const gliderAssociation = fleet.config.glider_association
      Object.keys(gliderAssociation).forEach((key, idx) => {
        if (offset?.length > idx) {
          gliderAssociation[key][2] = offset.at(idx)!
        }
      })
    }
  }

  static parseFleetParam(json?: string | null, FLE_TYP?: FleetType) {
    const conf = JSON.parse(json ?? '{}') as IFleetParamBase

    const fctBuilder =
      FLE_TYP == FleetType.COVERAGE
        ? getDefaultFleetParamCoverage
        : FLE_TYP == FleetType.FORMATION
        ? getDefaultFleetParamSimulation
        : getDefaultFleetParamBase
    //@ts-ignore
    const param = fctBuilder(conf) as IFleetParamCoverage | IFleetParamFormation | IFleetParamBase

    return param
  }

  static parseFleetIrisConfig(json?: string | null, FLE_TYP?: FleetType) {
    const conf = JSON.parse(json ?? '{}') as IFleetParamBase

    const fctBuilder =
      FLE_TYP == FleetType.COVERAGE
        ? getDefaultFleetIrisConfigCoverage
        : FLE_TYP == FleetType.FORMATION
        ? getDefaultFleetIrisConfigFormation
        : () => null
    //@ts-ignore
    const config = fctBuilder(conf) as IFleetIrisConfigCoverage | IFleetIrisConfigFormation | null

    return config
  }

  static createAloDbFleetArray(datas: any) {
    return new AloBaseArray(AloDbFleet, datas)
  }

  static createAloDbFleetWithDetail(data: any) {
    return new AloDbFleet({ ...data, detailLoaded: true })
  }
}
